import Content, { ContentNS } from "content/content";
import { PaxCheckDocumentSidesExpectation } from "api";
import useAllowedDocumentsForTravelDoc from "features/document-capture/hooks/useAllowedDocumentsForTravelDoc";
import ErrorBanner from "features/shared/components/banners/error-banner/ErrorBanner";
import SuccessBanner from "features/shared/components/banners/success-banner/SuccessBanner";
import LocalLink from "features/shared/components/link/LocalLink";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import useNavigateBackInfo, {
  PageType,
} from "features/shared/hooks/navigation/useNavigateBackInfo";
import useItemLevelAnalytics from "features/shared/hooks/analytics/useItemLevelAnalytics";
import { PAGE_TITLE_ID } from "features/document-page/hooks/useScrollToPageTitle";
import usePaxSession from "features/shared/hooks/usePaxSession";
import PageHeader from "features/shared/styles/page-header/PageHeader.styled";
import Body from "features/shared/styles/typography/Body.styled";
import { TertiaryHeader } from "features/shared/styles/typography/TertiaryHeader.styled";
import ChecklistItem, {
  DocumentRequirement,
  InfoItem,
} from "models/checklist/checklist-item/checklist-item";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import AllowedDocument from "models/documents/allowed-document";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import { FC } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useAppStore } from "store/store";
import { capitalizeTitle } from "utils";

import useErrors from "./hooks/useError";
// @ts-expect-error - This is how we utilize dynamic imports with WebPack NormalModuleReplacementPlugin
// See configuration in webpack.config.js
import DetailsContainer from "./styles/DetailsContainer-APP_CLIENT.styled";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import LocalImages from "assets/images/local-images";


interface ItemLevelContentProps {
  pageType: PageType;
  title: string;
  description: string;
  allowedDocs: AllowedDocument[];
}

interface ItemPageProps {
  item: ChecklistItem;
  forceDocumentType?: string;
}

const ItemLevelContent: FC<ItemLevelContentProps> = ({
  pageType,
  title,
  description,
  allowedDocs,
}) => {
  const backLinkInfo = useNavigateBackInfo(pageType);

  const [hasError, errorDescriptions, errorTitle, errorDescriptionTitles] =
    useErrors(allowedDocs);

  const paxSession = usePaxSession();

  const isWithPassportIcon = useLocation().pathname.includes("passport") && !!LocalImages.shared.images.icons.passport;

  return (
    <DetailsContainer>
      {backLinkInfo ? (
        <LocalLink to={backLinkInfo.to} state={backLinkInfo.state} backLink>
          {backLinkInfo.title}
        </LocalLink>
      ) : null}
      {hasError ? (
        <ErrorBanner
          title={errorTitle}
          descriptions={errorDescriptions}
          descriptionTitles={errorDescriptionTitles}
        />
      ) : null}
      {paxSession?.checklist.resolutionStatus ===
      ChecklistResolutionStatus.ReadyToFly ? (
        <SuccessBanner />
      ) : null}
      <PageHeader id={PAGE_TITLE_ID} $isWithIcon={isWithPassportIcon}>
        {isWithPassportIcon && (
          <img
            src={LocalImages.shared.images.icons.passport}
            alt="Passport Icon"
            className="passport-icon"
          />
        )}
        <TertiaryHeader>{`${title}`}</TertiaryHeader>
        {description !== "" ? <Body>{`${description}`}</Body> : null}
      </PageHeader>
      <Outlet />
    </DetailsContainer>
  );
};

const ItemPage: FC<ItemPageProps> = ({ item, forceDocumentType }) => {
  const textDocs = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.docs, ...args });

  const location = useLocation();

  const paxSession = usePaxSession();

  // items the flow skips can only be reached from the checklist
  const isSkippedInFlow =
    paxSession?.checklist.isCoveredByResolvedItems(item) === true;

  const docReqPageType =
    item instanceof DocumentRequirement && item.isTravelDocument
      ? PageType.TravelDocPage
      : isSkippedInFlow
      ? PageType.NonTravelDocPageFromChecklist
      : PageType.NonTravelDocPageWithinWizard;

  const pageType =
    item instanceof InfoItem
      ? PageType.NonTravelDocPageFromChecklist
      : docReqPageType;

  const allowedDocs =
    item instanceof DocumentRequirement
      ? item.allowedDocumentsSet.allowedDocuments
      : [];

  // On the item-resolver screen itself, forceDocumentType is just the
  // pre-selected default, not a confirmed choice — keep the combined title
  // there and only switch to document-specific copy on later screens.
  const selectedDocument =
    forceDocumentType && !location.pathname.includes("/item-resolver")
      ? allowedDocs.find((doc) => doc.type === forceDocumentType)
      : undefined;

  const itemTitle = item.title;

  // for info items we use item title itself, as
  // normally print "Verify {itemTitle}" and for
  // US ESTA, for example, this becomes "Verify US ESTA",
  // which is incorrect (as can't be verified)
  // Capitalized as a whole phrase, not per interpolated value: "item-title" is
  // a bare "{{itemTitle}}" for some clients and "Verify {{itemTitle}}" for
  // others. Capitalizing the result covers both — a leading placeholder gets
  // the capital it needs as a heading, while a leading literal is already
  // capitalized, leaving the title lowercase where it reads mid-sentence.
  const itemTitleToPrint = capitalizeTitle(
    `${
      selectedDocument
        ? selectedDocument.title
        : item instanceof InfoItem
          ? itemTitle
          : textDocs("item-title", {
              itemTitle: itemTitle,
            })
    }`
  );

  // Opt-in via locale key so clients without it keep the standard subtitle.
  const needsBackSideSubtitle =
    selectedDocument?.sidesExpectation ===
      PaxCheckDocumentSidesExpectation.CARD_BACK &&
    Content.shared.instance.exists("item-subtitle-back-side", {
      ns: ContentNS.docs,
    });

  const description =
    item instanceof DocumentRequirement &&
    item.itemType === ChecklistItemType.OnwardTicket &&
    location.pathname.includes("/manual-input")
      ? textDocs("onward-ticket.page-description")
      : selectedDocument
        ? textDocs(
            needsBackSideSubtitle ? "item-subtitle-back-side" : "item-subtitle",
            { itemTitle: selectedDocument.title }
          )
        : item.shortDescription
          ? item.shortDescription
          : textDocs("item-subtitle", {
              itemTitle: item.shortTitle || item.title,
            });

  return (
    <ItemLevelContent
      pageType={pageType}
      title={`${itemTitleToPrint}`}
      description={`${description}`}
      allowedDocs={allowedDocs}
    />
  );
};

const ItemTypePage: FC = () => {
  const textDocs = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.docs, ...args });

  const paxSession = usePaxSession();

  // if item type, only TRAVEL_DOC is currently supported.
  // if only 1 allowed travel document for session,
  // print its title (eg "Passport") instead of "Travel document"
  const allowedTravelDocs = useAllowedDocumentsForTravelDoc();
  const itemTitle =
    allowedTravelDocs.length === 1
      ? textDocs("item-title", {
          itemTitle: allowedTravelDocs[0].title,
        })
      : textDocs("itemTitle.TRAVEL_DOC");

  const description = textDocs("item-subtitle", {
    itemTitle: itemTitle,
  });

  return (
    <ItemLevelContent
      pageType={PageType.TravelDocPage}
      title={capitalizeTitle(`${itemTitle}`)}
      description={`${description}`}
      allowedDocs={
        paxSession?.checklist.allowedTravelDocumentsSet.allowedDocuments || []
      }
    />
  );
};

const ItemLevelPage: FC = () => {
  useItemLevelAnalytics();

  useAppStore((state) => state.locale);

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();

  const [searchParams] = useSearchParams();

  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  if (itemLevelNavigationState instanceof ItemNavigationState) {
    // itemId
    return (
      <ItemPage
        item={itemLevelNavigationState.item}
        forceDocumentType={itemLevelNavigationState.forceDocumentType}
      />
    );
  }

  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    // itemType - currently only supported for TRAVEL_DOC,
    // thus we don't pass itemType here atm
    return <ItemTypePage />;
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default ItemLevelPage;
